import React, { useEffect, useRef, useState } from "react";
import { PencilIcon } from "../Auth/onboarding/icons";

/**
 * Accepts what people actually paste: with or without the hash, upper or lower
 * case, and the 3-digit shorthand. Returns null when it isn't a colour yet, so
 * a half-typed value doesn't get committed.
 */
const normalizeHex = (raw) => {
  const text = String(raw).trim().replace(/^#/, "");

  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(text)) return null;

  const full =
    text.length === 3
      ? text
          .split("")
          .map((char) => char + char)
          .join("")
      : text;

  return `#${full.toLowerCase()}`;
};

export default function ColorField({ label, value, onChange }) {
  const inputRef = useRef(null);
  // What's in the box while typing. Kept separate from `value` so an
  // in-progress entry like "#6c4" isn't rejected mid-keystroke.
  const [draft, setDraft] = useState(value);

  // Follows the value when it changes elsewhere — a Reset, or the config
  // arriving from the API.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const openPicker = () => inputRef.current?.click();

  const handleTyping = (raw) => {
    setDraft(raw);

    // Apply as soon as it's a valid colour, so the preview tracks live.
    const hex = normalizeHex(raw);
    if (hex && hex !== value) onChange(hex);
  };

  // Anything still invalid when focus leaves reverts, rather than leaving the
  // field showing a colour that was never applied.
  const commit = () => {
    const hex = normalizeHex(draft);
    if (hex) {
      setDraft(hex.toUpperCase());
      if (hex !== value) onChange(hex);
    } else {
      setDraft(value);
    }
  };

  return (
    <div className="cz-color-field">
      <span className="cz-field-label">{label}</span>
      <span className="cz-color-swatch-row">
        <span
          className="cz-color-swatch"
          style={{ backgroundColor: value }}
          onClick={openPicker}
        />

        <input
          type="text"
          className="cz-color-hex"
          value={draft}
          aria-label={`${label} hex value`}
          spellCheck={false}
          autoComplete="off"
          maxLength={7}
          onChange={(e) => handleTyping(e.target.value)}
          onBlur={commit}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
              e.currentTarget.blur();
            }
          }}
        />

        <button
          type="button"
          className="cz-color-edit"
          onClick={openPicker}
          aria-label={`Pick ${label}`}
        >
          <PencilIcon />
        </button>

        <input
          ref={inputRef}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="cz-color-input"
          tabIndex={-1}
          aria-hidden="true"
        />
      </span>
    </div>
  );
}
